import { useContext, useEffect, useRef, useState, Suspense } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HotkeysProvider } from 'react-hotkeys-hook';
import { Box } from '@/UI/Box/Box';
import { CircularProgress } from '@/UI/CircularProgress/CircularProgress';
import { AppSnackbar } from '@/UI/AppSnackbar/AppSnackbar';
import { WeaveThemeProvider } from '@/UI/WeaveThemeProvider/WeaveThemeProvider';
import { useGlobalStore } from '@/state/global.state';
import PaymentSuccessHandler from '@/components/SubscriptionsAndPayments/PaymentSuccessHandler';
import { WeavyPaywallController } from '@/components/SubscriptionsAndPayments/WeavyPaywallController';
import { AuthContext } from './contexts/AuthContext';
import { CreditsProvider } from './services/CreditsContext';
import { QueryParamsProvider } from './contexts/QueryParamsContext';
import { I18N_KEYS } from './language/keys';
import { TourProvider } from './components/ProductTours/TourContext';
import { DebugProvider } from './hooks/useDebugContext';
import { SubscriptionPermissionsProvider } from './hooks/useSubscriptionPermissions/useSubscriptionPermissions';
import { Router } from './Router';
import { FlexCenHorVer, FlexCol } from './UI/styles';
import { SubscriptionType } from './types/shared';
import AppConfirmationDialog from './UI/AppConfirmationDialog/AppConfirmationDialog';
import { MarketingConsentModal } from './components/MarketingConsentModal/MarketingConsentModal';
import { identify } from './utils/analytics';
import { AppBanner } from './components/AppBanner/AppBanner';
import { useBanner, APP_BANNER_ID } from './hooks/useBanner';
import { TeamIcon } from './UI/Icons/TeamIcon';
import { useFeatureFlag } from './hooks/useFeatureFlag';
import { FF_INTERCOM_IDENTITY_JWT } from './consts/featureFlags';
import { useIntercomSession } from './hooks/useIntercomSession';

const intercomEnabled = import.meta.env.VITE_INTERCOM_ENABLED !== 'false';

export function App() {
  const { currentUser, isSigningIn, teamInviteSuccess, setTeamInviteSuccess } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const [loggedInUserInviteSuccess, setLoggedInUserInviteSuccess] = useState(false);
  const { offset } = useBanner(APP_BANNER_ID);
  const updateSnackbarData = useGlobalStore((state) => state.updateSnackbarData);

  const isIdentifiedRef = useRef(false);
  const { value: isIntercomIdentityJwtEnabled } = useFeatureFlag(FF_INTERCOM_IDENTITY_JWT);
  useIntercomSession(intercomEnabled ? currentUser : null, isIntercomIdentityJwtEnabled);

  /// identify user
  useEffect(() => {
    if (currentUser && !isIdentifiedRef.current) {
      const [firstName, ...lastNameParts] = currentUser.displayName?.split(' ') || [];
      identify(currentUser.uid, {
        email: currentUser.email || undefined,
        name: currentUser.displayName || undefined,
        firstName: firstName || undefined,
        lastName: lastNameParts.length ? lastNameParts.join(' ') : undefined,
        plan: currentUser.activeWorkspace?.subscription?.type || SubscriptionType.Free,
        planStartsAt: currentUser.activeWorkspace?.subscription?.startsAt
          ? new Date(currentUser.activeWorkspace.subscription.startsAt).toISOString()
          : undefined,
        credits: currentUser.activeWorkspace?.subscription?.credits,
        signInProvider: currentUser.providerData?.[0]?.providerId,
      });
      isIdentifiedRef.current = true;
    }
  }, [currentUser]);

  // handle accept team invite for logged in user
  useEffect(() => {
    const acceptInvitation = searchParams.get('acceptInvitation');
    if (acceptInvitation) {
      setLoggedInUserInviteSuccess(true);
      window.history.replaceState(undefined, '', window.location.pathname);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!teamInviteSuccess && !loggedInUserInviteSuccess) return;
    updateSnackbarData({
      text: t(I18N_KEYS.SETTINGS.TEAM.JOIN_TEAM_SUCCESS),
      isOpen: true,
      severity: 'success',
      icon: <TeamIcon width={24} height={24} />,
      duration: 3000,
      action: null,
    });
    setLoggedInUserInviteSuccess(false);
    setTeamInviteSuccess(false);
  }, [teamInviteSuccess, loggedInUserInviteSuccess, setTeamInviteSuccess, updateSnackbarData, t]);

  return (
    <DebugProvider>
      <WeaveThemeProvider>
        <AppSnackbar />
        <TourProvider>
          <CreditsProvider>
            <WeavyPaywallController />
            <QueryParamsProvider>
              <SubscriptionPermissionsProvider user={currentUser}>
                <HotkeysProvider>
                  {currentUser && <PaymentSuccessHandler />}
                  <AppBanner shouldDisplayClose={false} />
                  <FlexCol sx={{ height: `calc(100vh - ${offset}px)` }} id="weavy-main">
                    <Box component="main" sx={{ flexGrow: 1, overflow: 'auto' }}>
                      <Suspense
                        fallback={
                          <FlexCenHorVer sx={{ height: '100%' }}>
                            <CircularProgress color="inherit" />
                          </FlexCenHorVer>
                        }
                      >
                        <Router currentUser={currentUser} isSigningIn={isSigningIn} />
                      </Suspense>
                    </Box>
                  </FlexCol>
                  <MarketingConsentModal />
                </HotkeysProvider>
              </SubscriptionPermissionsProvider>
            </QueryParamsProvider>
          </CreditsProvider>
        </TourProvider>
        <AppConfirmationDialog />
      </WeaveThemeProvider>
    </DebugProvider>
  );
}
